import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import { C, ACCENT } from '../../theme';
import { useToast } from '../../components/Toast';
import { FileText, Rocket, CheckCircle, Flag, Archive, Trash2, Copy } from 'lucide-react';

const STATUS_COLOR   = { draft:'#8aa0b8', active:'#0090d0', approved:'#2a8a5a', completed:'#6a8aaa' };
const STATUS_VARIANT = { draft:'neutral', active:'info', approved:'success', completed:'neutral' };
const STATUS_ICON    = { draft:FileText, active:Rocket, approved:CheckCircle, completed:Flag };
const SIcon = ({s}) => { const I = STATUS_ICON[s]; return I ? <I size={11} style={{verticalAlign:'middle',marginRight:3}}/> : null; };

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(80,120,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={onClose}>
      <div style={C.modal} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:'#1a2a3a' }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#8aa0b8', fontSize:22, cursor:'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function BrandDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, isAdmin, canCreatePlans, canApprovePlans, canManageTaxonomy, taxonomyEnabled } = useAuth();
  const { showToast } = useToast();

  const [brand, setBrand] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name:'', year: new Date().getFullYear() });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteBrand, setConfirmDeleteBrand] = useState(false);
  const [deleteBrandError, setDeleteBrandError] = useState(null);
  const [taxonomyModal, setTaxonomyModal] = useState(false);
  const [taxonomyEntries, setTaxonomyEntries] = useState([]);
  const [taxonomySearch, setTaxonomySearch] = useState('');
  const [cloneConfirm, setCloneConfirm] = useState(null);
  const [cloning, setCloning] = useState(false);

  async function load() {
    try {
      const b = await api.getBrand(id);
      setBrand(b); setPlans(b.plans||[]);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [id]);

  async function createPlan() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const plan = await api.createPlan({ brand_id: parseInt(id), name: form.name, year: form.year, data:{} });
      navigate(`/plans/${plan.id}`);
    } catch(e) { showToast(e.message, 'danger'); }
    finally { setSaving(false); }
  }

  async function handleApprove(e, planId) {
    e.stopPropagation();
    try {
      await api.updatePlan(planId, { status: 'approved' });
      showToast('Plan approved', 'success');
      load();
    } catch(e) { showToast(e.message, 'danger'); }
  }

  async function handleArchivePlan(e, plan) {
    e.stopPropagation();
    try {
      const res = await api.archivePlan(plan.id);
      showToast(res.archived ? 'Plan archived' : 'Plan unarchived', 'success');
      load();
    } catch(e) { showToast(e.message, 'danger'); }
  }

  async function handleDeletePlan() {
    if (!confirmDelete) return;
    try {
      await api.deletePlan(confirmDelete.id);
      setConfirmDelete(null);
      showToast('Plan deleted', 'success');
      load();
    } catch(e) { showToast(e.message, 'danger'); setConfirmDelete(null); }
  }

  function openTaxonomyModal() {
    if (!taxonomyEntries.length) api.getTaxonomyEntries().then(setTaxonomyEntries).catch(() => {});
    setTaxonomySearch('');
    setTaxonomyModal(true);
  }

  async function linkTaxonomyEntry(entryId) {
    try {
      await api.setBrandTaxonomyLink(parseInt(id), entryId);
      showToast(entryId ? 'Taxonomy entry linked' : 'Taxonomy link cleared', 'success');
      setTaxonomyModal(false);
      load();
    } catch (e) { showToast(e.message, 'danger'); }
  }

  async function handleCloneConfirmed() {
    if (!cloneConfirm) return;
    setCloning(true);
    try {
      const clone = await api.clonePlan(cloneConfirm.id);
      setCloneConfirm(null);
      navigate(`/plans/${clone.id}`);
    } catch (e) { showToast(e.message, 'danger'); setCloning(false); }
  }

  async function handleDeleteBrand() {
    setDeleteBrandError(null);
    try {
      await api.deleteBrand(parseInt(id));
      showToast('Brand deleted', 'success');
      if (brand.client_id) navigate(`/clients/${brand.client_id}`);
      else navigate('/brands');
    } catch(e) { setDeleteBrandError(e.message); }
  }

  const visiblePlans = showArchived ? plans : plans.filter(p => !p.archived);
  const filtered = filter === 'all' ? visiblePlans : visiblePlans.filter(p => p.status === filter);
  const archivedCount = plans.filter(p => p.archived).length;

  if (loading) return <div style={{...C.page, color:'#8aa0b8'}}>Loading…</div>;
  if (!brand) return <div style={{...C.page, color:'#d04040'}}>Brand not found.</div>;

  return (
    <div style={C.page}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:28 }}>
        {brand.client_id ? (
          <button onClick={()=>navigate(`/clients/${brand.client_id}`)} style={{ background:'transparent', border:`1px solid #dce8f0`, borderRadius:7, padding:'6px 14px', fontSize:12, color:'#4a6a8a', cursor:'pointer' }}>← Client</button>
        ) : (
          <button onClick={()=>navigate('/brands')} style={{ background:'transparent', border:`1px solid #dce8f0`, borderRadius:7, padding:'6px 14px', fontSize:12, color:'#4a6a8a', cursor:'pointer' }}>← Brands</button>
        )}
        <div>
          <h1 style={C.title}>{brand.name}</h1>
          <div style={{ fontSize:12, color:'#8aa0b8', marginTop:2 }}>{visiblePlans.length} plans</div>
        </div>
        <div style={{ flex:1 }}/>
        {taxonomyEnabled && canManageTaxonomy && (
          <button style={C.btnSecondary} onClick={openTaxonomyModal} title="Link this brand to a taxonomy entry">
            Taxonomy: {brand.taxonomy_entry ? brand.taxonomy_entry.label : <span style={{color:'#8aa0b8'}}>not linked</span>}
          </button>
        )}
        {isAdmin && (
          <button style={C.btnDanger} onClick={()=>{ setDeleteBrandError(null); setConfirmDeleteBrand(true); }}>
            <Trash2 size={14} style={{marginRight:5,verticalAlign:'middle'}}/>Delete Brand
          </button>
        )}
        {canCreatePlans && (
          <button style={C.btnPrimary} onClick={()=>setModal(true)}>+ New Plan</button>
        )}
      </div>

      {/* Status filter + show archived toggle */}
      <div style={{ display:'flex', gap:6, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
        {['all','draft','active','approved','completed'].map(s=>(
          <button key={s} onClick={()=>setFilter(s)} style={{
            padding:'5px 14px', borderRadius:20, fontSize:12, fontWeight:600,
            cursor:'pointer', border:'1px solid',
            borderColor: filter===s ? ACCENT : '#dce8f0',
            background: filter===s ? `${ACCENT}15` : 'transparent',
            color: filter===s ? ACCENT : '#8aa0b8',
          }}>
            {s==='all' ? `All (${visiblePlans.length})` : <><SIcon s={s}/>{s} ({visiblePlans.filter(p=>p.status===s).length})</>}
          </button>
        ))}
        {isAdmin && archivedCount > 0 && (
          <button onClick={()=>setShowArchived(v=>!v)} style={{
            marginLeft:'auto', padding:'5px 14px', borderRadius:20, fontSize:12, fontWeight:600,
            cursor:'pointer', border:'1px solid',
            borderColor: showArchived ? '#8aa0b8' : '#dce8f0',
            background: showArchived ? 'rgba(138,160,184,0.12)' : 'transparent',
            color: '#8aa0b8',
            display:'flex', alignItems:'center', gap:5,
          }}>
            <Archive size={12}/>{showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
          </button>
        )}
      </div>

      {/* Plans list */}
      {filtered.length===0 ? (
        <div style={C.empty}>{filter==='all'?'No plans yet. Create the first plan for this brand.':`No ${filter} plans.`}</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8, width:'100%' }}>
          {filtered.map(plan=>(
            <div key={plan.id}
              onClick={()=>navigate(`/plans/${plan.id}`)}
              style={{ display:'flex', alignItems:'center', gap:14, background: plan.archived ? 'rgba(240,244,248,0.7)' : 'rgba(255,255,255,0.75)', border:'1px solid #dce8f0', borderRadius:12, padding:'14px 18px', cursor:'pointer', transition:'all .15s', boxShadow:'0 1px 4px rgba(80,120,160,0.06)', width:'100%', boxSizing:'border-box', opacity: plan.archived ? 0.7 : 1 }}
              onMouseEnter={e=>{e.currentTarget.style.boxShadow='0 4px 16px rgba(0,144,208,0.1)';e.currentTarget.style.borderColor='#0090d040';}}
              onMouseLeave={e=>{e.currentTarget.style.boxShadow='0 1px 4px rgba(80,120,160,0.06)';e.currentTarget.style.borderColor='#dce8f0';}}>
              <div style={{ width:4, borderRadius:4, alignSelf:'stretch', flexShrink:0, background: plan.archived ? '#c8d8e4' : (STATUS_COLOR[plan.status]||'#8aa0b8') }}/>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                  <span style={{ fontWeight:700, fontSize:14, color:'#1a2a3a' }}>{plan.name}</span>
                  {plan.archived ? (
                    <span style={C.statusRow()}>
                      <Archive size={10} style={{marginRight:3, color:'#8aa0b8'}}/>
                      <span style={{color:'#8aa0b8', fontSize:11}}>archived</span>
                    </span>
                  ) : (
                    <span style={C.statusRow()}>
                      <span style={C.statusDot(STATUS_VARIANT[plan.status]||'neutral')}/>
                      {plan.status}
                    </span>
                  )}
                </div>
                <div style={{ fontSize:11, color:'#8aa0b8' }}>
                  Created {new Date(plan.created_at).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}
                </div>
              </div>
              <div style={{ display:'flex', gap:6, flexShrink:0 }} onClick={e=>e.stopPropagation()}>
                {plan.status==='draft' && canApprovePlans && !plan.archived && (
                  <button style={C.btnSecondary} onClick={e=>handleApprove(e,plan.id)}>Approve</button>
                )}
                {canCreatePlans && (
                  <button style={C.btnSecondary} onClick={e=>{e.stopPropagation(); setCloneConfirm(plan);}} title="Clone">
                    <Copy size={13}/>
                  </button>
                )}
                {isAdmin && (
                  <button style={C.btnSecondary} onClick={e=>handleArchivePlan(e,plan)} title={plan.archived ? 'Unarchive' : 'Archive'}>
                    <Archive size={13}/>
                  </button>
                )}
                {isAdmin && (
                  <button style={C.btnDanger} onClick={e=>{e.stopPropagation();setConfirmDelete(plan);}}>
                    <Trash2 size={13}/>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New plan modal */}
      {modal && (
        <Modal title="New Plan" onClose={()=>setModal(false)}>
          <label style={{ display:'block', marginBottom:14 }}>
            <span style={C.label}>Plan Name *</span>
            <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Q2 2026 Campaign" style={C.input} autoFocus/>
          </label>
          <label style={{ display:'block', marginBottom:22 }}>
            <span style={C.label}>Year</span>
            <input type="number" value={form.year} onChange={e=>setForm(p=>({...p,year:+e.target.value}))} style={C.input}/>
          </label>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={C.btnSecondary} onClick={()=>setModal(false)}>Cancel</button>
            <button style={C.btnPrimary} onClick={createPlan} disabled={saving}>{saving?'Creating…':'Create & Open'}</button>
          </div>
        </Modal>
      )}

      {/* Delete plan confirmation */}
      {confirmDelete && (
        <Modal title="Delete Plan" onClose={()=>setConfirmDelete(null)}>
          <p style={{ margin:'0 0 22px', color:'#1a2a3a', fontSize:14 }}>
            Delete <strong>{confirmDelete.name}</strong>? This cannot be undone.
          </p>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={C.btnSecondary} onClick={()=>setConfirmDelete(null)}>Cancel</button>
            <button style={C.btnDanger} onClick={handleDeletePlan}>Delete</button>
          </div>
        </Modal>
      )}

      {/* Delete brand confirmation */}
      {confirmDeleteBrand && (
        <Modal title="Delete Brand" onClose={()=>setConfirmDeleteBrand(false)}>
          <p style={{ margin:'0 0 16px', color:'#1a2a3a', fontSize:14 }}>
            Delete brand <strong>{brand.name}</strong>? This cannot be undone.
          </p>
          {deleteBrandError && <div style={{...C.feedback('danger'), marginBottom:16}}>{deleteBrandError}</div>}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={C.btnSecondary} onClick={()=>setConfirmDeleteBrand(false)}>Cancel</button>
            <button style={C.btnDanger} onClick={handleDeleteBrand}>Delete Brand</button>
          </div>
        </Modal>
      )}

      {cloneConfirm && (
        <Modal title="Clone Plan" onClose={()=>!cloning && setCloneConfirm(null)}>
          <p style={{ margin:'0 0 16px', color:'#1a2a3a', fontSize:14 }}>
            Clone <strong>{cloneConfirm.name}</strong>? Only the saved version is copied — any unsaved changes in an open editor are not included. The new plan is a draft under this brand.
          </p>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={C.btnSecondary} onClick={()=>setCloneConfirm(null)} disabled={cloning}>Cancel</button>
            <button style={C.btnPrimary} onClick={handleCloneConfirmed} disabled={cloning}>{cloning ? 'Cloning…' : 'Clone'}</button>
          </div>
        </Modal>
      )}

      {taxonomyModal && (
        <Modal title="Link Taxonomy Entry" onClose={()=>setTaxonomyModal(false)}>
          <input placeholder="Search client / brand / product…" value={taxonomySearch} onChange={e=>setTaxonomySearch(e.target.value)}
            style={{ ...C.input, marginBottom:14 }} autoFocus/>
          <div style={{ maxHeight:320, overflowY:'auto', border:'1px solid #dce8f0', borderRadius:8 }}>
            {brand.taxonomy_entry_id && (
              <div onClick={()=>linkTaxonomyEntry(null)} style={{ padding:'10px 14px', cursor:'pointer', color:'#a02020', fontSize:13, borderBottom:'1px solid #eef4f8' }}>
                ✕ Clear current link
              </div>
            )}
            {taxonomyEntries
              .filter(e => !taxonomySearch || `${e.client_name} ${e.brand_name} ${e.sub_brand_name} ${e.product_name} ${e.category_name}`.toLowerCase().includes(taxonomySearch.toLowerCase()))
              .map(e => (
                <div key={e.id} onClick={()=>linkTaxonomyEntry(e.id)}
                  style={{ padding:'10px 14px', cursor:'pointer', fontSize:13, borderBottom:'1px solid #eef4f8',
                    background: brand.taxonomy_entry_id===e.id ? `${ACCENT}10` : 'transparent' }}>
                  {e.client_name} / {e.brand_name} / {e.sub_brand_name} / {e.product_name} / {e.category_name}
                </div>
              ))}
            {taxonomyEntries.length===0 && <div style={{ padding:14, color:'#8aa0b8', fontSize:12 }}>No taxonomy entries imported yet.</div>}
          </div>
        </Modal>
      )}
    </div>
  );
}
